/**
 * Transcription processing and status management
 */
import { showToast } from '../utils/toast-utils.js';
import { UI_STATUS, TERMINAL_STATUSES, STATUS_TEST_ID } from '../utils/status-constants.js';
import { isLicenseGateError, handleLicenseGateError } from '../utils/license-gate-utils.js';

// Track files that are being processed
const processingFiles = new Set();
const NO_ACTIVE_JOB_POLLS_BEFORE_STUCK = 3;
const noActiveJobCounts = new Map();

/**
 * Builds the `data-test` value of a file-row element, matching the names
 * templates/file_explorer.html emits so a re-rendered row stays addressable.
 * @param {string} element - Element name, e.g. 'view-transcription-button'.
 * @param {string} fileId - File id.
 * @returns {string} e.g. 'projects.file-explorer.view-transcription-button-42'.
 */
const rowTestId = (element, fileId) => `projects.file-explorer.${element}-${fileId}`;

/**
 * Locates a row's cells by the content they hold instead of by column position.
 * The number of columns varies with the viewer's permissions (the Preview column
 * is only rendered for `view_transcription`), so a `td:nth-child(n)` index
 * silently resolves to the wrong cell as soon as a column is added.
 */
const rowLanguageCell = (fileRow) => fileRow.querySelector('.language-badge')?.closest('td');
const rowStatusCell = (fileRow) => fileRow.querySelector('.file-status')?.closest('td');
const rowActionsContainer = (fileRow) => fileRow.querySelector('.table-action-group');

/** Badge appearance per status; anything unrecognized is shown as in progress. */
const IN_PROGRESS_BADGE = Object.freeze({
  modifier: 'bg-warning',
  icon: 'loader-2',
  iconClass: 'lucide-spin me-1',
});

const STATUS_BADGE = Object.freeze({
  [UI_STATUS.COMPLETED]: { modifier: 'bg-success', icon: 'check-circle' },
  [UI_STATUS.FAILED]: { modifier: 'bg-danger', icon: 'triangle-alert' },
  [UI_STATUS.NOT_STARTED]: { modifier: 'bg-secondary', icon: 'clock' },
});

/** The CSRF token the row's transcribe and retry buttons post with. */
const currentCsrfToken = () => document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '';

/**
 * A Lucide placeholder icon, wrapped in `.badge-icon` unless it carries its own
 * classes (the spinner does), exactly as the template renders them.
 * @param {string} name - Lucide icon name.
 * @param {string} [className] - Classes for the icon itself.
 * @returns {HTMLElement} The node to append.
 */
function lucideIcon(name, className) {
  const icon = document.createElement('i');
  icon.setAttribute('data-lucide', name);

  if (className) {
    icon.className = className;
    return icon;
  }

  const wrapper = document.createElement('span');
  wrapper.className = 'badge-icon';
  wrapper.appendChild(icon);

  return wrapper;
}

/**
 * A file's status badge, identical to the server-rendered one.
 * @param {string} fileId - File id.
 * @param {string} status - A UI_STATUS value.
 * @returns {HTMLElement} The badge.
 */
function statusBadge(fileId, status) {
  const kind = STATUS_TEST_ID[status] ?? STATUS_TEST_ID[UI_STATUS.IN_PROGRESS];
  const { modifier, icon, iconClass } = STATUS_BADGE[status] ?? IN_PROGRESS_BADGE;

  const badge = document.createElement('span');
  badge.className = `file-status status-${kind} badge ${modifier}`;
  badge.setAttribute('data-test', rowTestId(`file-status-${kind}`, fileId));
  badge.appendChild(lucideIcon(icon, iconClass));
  badge.appendChild(document.createTextNode(` ${status}`));

  return badge;
}

/**
 * The disabled "Processing..." button shown while a job runs.
 * @param {string} fileId - File id.
 * @param {string} status - A UI_STATUS value; decides which `data-test` it carries.
 * @returns {HTMLButtonElement} The button.
 */
function processingButton(fileId, status) {
  const button = document.createElement('button');
  button.className = 'btn file-list-actions-btn-sm btn-sm btn-warning action-btn';
  button.disabled = true;
  button.setAttribute('data-file-id', fileId);
  button.setAttribute(
    'data-test',
    rowTestId(status === UI_STATUS.API_PROCESSING ? 'api-processing-button' : 'in-progress-button', fileId)
  );
  const spinner = lucideIcon('loader-2', 'lucide-spin me-1');
  spinner.setAttribute('role', 'status');
  button.appendChild(spinner);
  button.appendChild(document.createTextNode(' Processing...'));

  return button;
}

/**
 * The "Retry" button offered for a failed job.
 * @param {string} fileId - File id.
 * @returns {HTMLButtonElement} The button.
 */
function retryButton(fileId) {
  const button = document.createElement('button');
  button.type = 'button';
  button.className = 'btn file-list-actions-btn-sm btn-sm btn-primary transcribe-btn action-btn';
  button.setAttribute('data-file-id', fileId);
  button.setAttribute('data-test', rowTestId('retry-transcription-button', fileId));
  button.setAttribute('data-csrf', currentCsrfToken());
  button.appendChild(lucideIcon('rotate-ccw', 'mr-0'));
  button.appendChild(document.createTextNode(' Retry'));

  return button;
}

/**
 * Replaces a row's status cell with the badge for `status`.
 * @param {HTMLElement} statusCell - The row's third cell.
 * @param {string} fileId - File id.
 * @param {string} status - A UI_STATUS value.
 */
function renderStatusCell(statusCell, fileId, status) {
  statusCell.replaceChildren(statusBadge(fileId, status));

  if (window.lucide) {
    window.lucide.createIcons({ nodes: [statusCell] });
  }
}

function attachTranscribeListenerToButton(btn) {
  btn.addEventListener('click', function (e) {
    e.preventDefault();

    const fileId = this.getAttribute('data-file-id');
    const csrfToken = this.getAttribute('data-csrf');

    const originalText = this.innerHTML;
    this.innerHTML = '<i data-lucide="loader-2" class="lucide-spin me-1"></i> Starting...';
    if (window.lucide) window.lucide.createIcons({ nodes: [this] });
    this.disabled = true;

    fetch(`/files/${fileId}/transcribe`, {
      method: 'POST',
      headers: {
        'X-Requested-With': 'XMLHttpRequest',
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: `csrf_token=${csrfToken || ''}`,
    })
      .then((response) => {
        return response.json().then((data) => {
          return {
            ok: response.ok,
            status: response.status,
            data: data,
          };
        });
      })
      .then((result) => {
        if (result.ok && result.data.status === 'success') {
          if (this.parentNode) {
            const processing = processingButton(fileId, UI_STATUS.IN_PROGRESS);
            this.replaceWith(processing);
            if (window.lucide) window.lucide.createIcons({ nodes: [processing] });
          }

          const fileRow = document.querySelector(`tr[data-file-id="${fileId}"]`);
          if (fileRow) {
            const statusCell = rowStatusCell(fileRow);
            if (statusCell) {
              renderStatusCell(statusCell, fileId, UI_STATUS.IN_PROGRESS);
            }
          }

          processingFiles.add(fileId);
          checkFileStatus(fileId);
        } else if (isLicenseGateError(result.status, result.data)) {
          handleLicenseGateError(result.status, result.data);

          // eslint-disable-next-line no-unsanitized/property
          this.innerHTML = originalText;
          this.disabled = false;
        } else {
          showToast(result.data.message || 'Failed to start transcription', 'error', {
            dataTest: 'projects.project-files.file-failed-to-start-transcription-error-toast',
          });

          // eslint-disable-next-line no-unsanitized/property
          this.innerHTML = originalText;
          this.disabled = false;
        }
      })
      .catch((error) => {
        console.error('Error starting transcription:', error);
        showToast(`Failed to start transcription: ${error.message}`, 'error', {
          dataTest: 'projects.project-files.file-failed-to-start-transcription-error-toast',
        });

        // eslint-disable-next-line no-unsanitized/property
        this.innerHTML = originalText;
        this.disabled = false;
      });
  });
}

export function attachTranscribeListeners() {
  document.querySelectorAll('.transcribe-btn').forEach((btn) => {
    const newBtn = btn.cloneNode(true);
    btn.parentNode.replaceChild(newBtn, btn);
    attachTranscribeListenerToButton(newBtn);
  });
}

function renderFileAsStuck(fileId, fileRow) {
  if (!fileRow) return;

  const statusCell = rowStatusCell(fileRow);
  if (statusCell) {
    renderStatusCell(statusCell, fileId, UI_STATUS.FAILED);
  }

  const actionsContainer = rowActionsContainer(fileRow);
  if (actionsContainer) {
    const infoBtn = actionsContainer.querySelector('.file-info-btn');
    const deleteBtn = actionsContainer.querySelector('.delete-file-btn');
    actionsContainer.textContent = '';

    if (infoBtn) actionsContainer.appendChild(infoBtn);

    const retryBtn = retryButton(fileId);
    actionsContainer.appendChild(retryBtn);

    if (deleteBtn) actionsContainer.appendChild(deleteBtn);

    if (typeof lucide !== 'undefined') {
      lucide.createIcons({ nodes: [actionsContainer] });
    }
    attachTranscribeListenerToButton(retryBtn);
    if (infoBtn && window.attachFileInfoListenerToButton) {
      window.attachFileInfoListenerToButton(infoBtn);
    }
  }
}

// Function to check status of a file that's being processed
export function checkFileStatus(fileId) {
  if (!fileId) return;

  // Make AJAX request to check job status
  fetch(`/files/${fileId}/job_status`, {
    method: 'GET',
    headers: {
      'X-Requested-With': 'XMLHttpRequest',
    },
  })
    .then((response) => response.json())
    .then((data) => {
      // Find the file row
      const fileRow = document.querySelector(`tr[data-file-id="${fileId}"]`);
      if (!fileRow) {
        return;
      }

      // Update language cell if language was auto-detected
      if (data.language_code && data.language_name) {
        const languageCell = rowLanguageCell(fileRow);
        if (languageCell) {
          const languageBadge = document.createElement('span');
          languageBadge.className = 'badge bg-light text-dark border language-badge';
          languageBadge.setAttribute('data-test', rowTestId('language-badge', fileId));
          languageBadge.appendChild(lucideIcon('globe'));
          languageBadge.appendChild(document.createTextNode(` ${data.language_name}`));
          languageCell.replaceChildren(languageBadge);

          if (typeof lucide !== 'undefined') {
            lucide.createIcons({ nodes: [languageCell] });
          }
        }
      }

      // Update status cell
      const statusCell = rowStatusCell(fileRow);
      if (statusCell) {
        if (data.status === UI_STATUS.COMPLETED) {
          // Stop tracking this file
          processingFiles.delete(fileId);

          // Apply highlight effect
          fileRow.classList.add('highlight-completed');
          setTimeout(() => {
            fileRow.classList.remove('highlight-completed');
          }, 3000);
        } else if (data.status === UI_STATUS.FAILED) {
          processingFiles.delete(fileId);
        }

        renderStatusCell(statusCell, fileId, data.status);
      }

      // Update actions cell
      const actionsContainer = rowActionsContainer(fileRow);
      if (actionsContainer) {
        // Get the existing info button if it exists, or create a new one
        let infoBtn = actionsContainer.querySelector('.file-info-btn');
        const deleteBtn = actionsContainer.querySelector('.delete-file-btn');

        // Clear container safely
        actionsContainer.textContent = '';

        if (!infoBtn) {
          // Create the info button if it doesn't exist yet using DOM methods
          infoBtn = document.createElement('button');
          infoBtn.type = 'button';
          infoBtn.className = 'btn btn-sm custom-info-btn file-info-btn';
          infoBtn.setAttribute('data-bs-toggle', 'modal');
          infoBtn.setAttribute('data-bs-target', '#fileInfoModal');
          infoBtn.setAttribute('data-file-id', fileId);
          infoBtn.setAttribute('data-test', rowTestId('file-info-button', fileId));
          const infoIcon = document.createElement('i');
          infoIcon.setAttribute('data-lucide', 'info');
          infoBtn.appendChild(infoIcon);
        }

        // Add info button first
        actionsContainer.appendChild(infoBtn);

        // Build action button based on status using DOM methods
        if (data.status === UI_STATUS.COMPLETED) {
          // Create view transcription link
          const viewLink = document.createElement('a');
          viewLink.href = '/files/' + encodeURIComponent(fileId) + '/transcription';
          viewLink.className = 'btn file-list-actions-btn-sm btn-sm btn-primary action-btn';
          viewLink.setAttribute('data-test', rowTestId('view-transcription-button', fileId));
          const viewIcon = document.createElement('i');
          viewIcon.setAttribute('data-lucide', 'file-text');
          viewLink.appendChild(viewIcon);
          viewLink.appendChild(document.createTextNode(' View Transcription'));
          actionsContainer.appendChild(viewLink);
        } else if (data.status === UI_STATUS.NOT_STARTED) {
          // Create transcribe button
          const transcribeBtn = document.createElement('button');
          transcribeBtn.type = 'button';
          transcribeBtn.className = 'btn file-list-actions-btn-sm btn-sm btn-success transcribe-btn action-btn';
          transcribeBtn.setAttribute('data-file-id', fileId);
          transcribeBtn.setAttribute('data-test', rowTestId('transcribe-button', fileId));
          transcribeBtn.setAttribute('data-csrf', currentCsrfToken());
          const transcribeIcon = document.createElement('i');
          transcribeIcon.setAttribute('data-lucide', 'mic');
          transcribeBtn.appendChild(transcribeIcon);
          transcribeBtn.appendChild(document.createTextNode(' Transcribe'));
          actionsContainer.appendChild(transcribeBtn);
        } else if (data.status === UI_STATUS.FAILED) {
          actionsContainer.appendChild(retryButton(fileId));
        } else {
          actionsContainer.appendChild(processingButton(fileId, data.status));
        }

        // Re-add delete button if it existed
        if (deleteBtn) {
          actionsContainer.appendChild(deleteBtn);
        }

        // Render Lucide icons in newly created DOM elements
        if (typeof lucide !== 'undefined') {
          lucide.createIcons({ nodes: [actionsContainer] });
        }

        const newTranscribeBtn = actionsContainer.querySelector('.transcribe-btn');
        if (newTranscribeBtn) {
          attachTranscribeListenerToButton(newTranscribeBtn);
        }
        if (infoBtn && window.attachFileInfoListenerToButton) {
          window.attachFileInfoListenerToButton(infoBtn);
        }
      }

      if (processingFiles.has(fileId)) {
        if (TERMINAL_STATUSES.includes(data.status)) {
          processingFiles.delete(fileId);
          noActiveJobCounts.delete(fileId);
        } else if (data.has_active_job === false) {
          const count = (noActiveJobCounts.get(fileId) || 0) + 1;
          noActiveJobCounts.set(fileId, count);

          if (count >= NO_ACTIVE_JOB_POLLS_BEFORE_STUCK) {
            console.warn(`File ${fileId} has no active job but status "${data.status}", treating as stuck`);
            processingFiles.delete(fileId);
            noActiveJobCounts.delete(fileId);
            showToast('Transcription appears stuck. You can retry.', 'error', {
              dataTest: 'projects.project-files.file-transcription-appears-stuck-error-toast',
            });
            renderFileAsStuck(fileId, fileRow);
          } else {
            setTimeout(() => checkFileStatus(fileId), 2000);
          }
        } else {
          noActiveJobCounts.delete(fileId);
          setTimeout(() => checkFileStatus(fileId), 2000);
        }
      }
    })
    .catch((error) => {
      console.error('Error checking file status:', error);
      // Remove from processing files on repeated errors to prevent infinite polling
      processingFiles.delete(fileId);
    });
}

function getProcessingFileIds() {
  const fileIds = new Set();

  document.querySelectorAll('.file-list-actions-btn-sm.btn-warning[disabled]').forEach((btn) => {
    const fileId = btn.getAttribute('data-file-id');
    if (fileId) fileIds.add(fileId);
  });

  document.querySelectorAll('.file-status.status-in-progress').forEach((badge) => {
    const row = badge.closest('tr[data-file-id]');
    if (row) {
      const fileId = row.getAttribute('data-file-id');
      if (fileId) fileIds.add(fileId);
    }
  });

  return fileIds;
}

function recheckProcessingFiles() {
  const fileIds = getProcessingFileIds();
  for (const fileId of fileIds) {
    if (!processingFiles.has(fileId)) {
      processingFiles.add(fileId);
      checkFileStatus(fileId);
    }
  }
}

export function initializeTranscriptionHandlers() {
  attachTranscribeListeners();
  recheckProcessingFiles();

  window.addEventListener('pageshow', (event) => {
    if (event.persisted) {
      recheckProcessingFiles();
    }
  });

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      recheckProcessingFiles();
    }
  });
}

// Make functions globally available
window.attachTranscribeListeners = attachTranscribeListeners;
